import type * as SentryTypes from '@sentry/react'
import type { Span } from '@sentry/react'

type SentryModule = typeof SentryTypes

const MAX_QUEUED_CALLS = 100

let mod: SentryModule | null = null
let loading: Promise<SentryModule | null> | null = null
const queued: Array<(sentry: SentryModule) => void> = []

export function getSentry(): SentryModule | null {
  return mod
}

export function isSentryLoading(): boolean {
  return loading != null
}

export function loadSentry(): Promise<SentryModule | null> {
  loading ??= import('@sentry/react')
    .then((loaded) => {
      mod = loaded
      for (const call of queued.splice(0)) call(loaded)
      return loaded
    })
    .catch(() => null)
  return loading
}

export function withSentry(call: (sentry: SentryModule) => void): void {
  if (mod) {
    call(mod)
    return
  }
  if (loading && queued.length < MAX_QUEUED_CALLS) queued.push(call)
}

const noopSpan = new Proxy(
  {},
  { get: () => () => undefined },
) as unknown as Span

export function startSentrySpan<T>(
  options: { name: string; op: string; attributes?: Record<string, unknown> },
  fn: (span: Span) => T,
): T {
  if (mod) {
    return mod.startSpan(
      options as Parameters<SentryModule['startSpan']>[0],
      fn,
    )
  }
  return fn(noopSpan)
}
